import React, { useEffect, useState } from "react";
import {
  Form,
  Row,
  Col,
  Button,
  Card,
  Modal,
  CardHeader,
  CardBody,
} from "react-bootstrap";

import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import Select from "react-select";
import GalleryUpload from "./GalleryUpload";

export default function EditReligiousAffiliation() {
  const { id } = useParams();

  // ==========================================================
  // STATE
  // ==========================================================

  const [families, setFamilies] = useState([]);
  const [faiths, setFaiths] = useState([]);
  const [prayerPlaces, setPrayerPlaces] = useState([]);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [affiliationId, setAffiliationId] = useState(null);
  const [images, setImages] = useState([]);

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoUploading, setVideoUploading] = useState(false);

  const [showPrayerPlaceModal, setShowPrayerPlaceModal] =
    useState(false);

  const [formErrors, setFormErrors] = useState({});

  const [form, setForm] = useState({
    person_id: "",
    faith_id: "",
    place_id: "",
    baptized: 0,
    comments: "",
    video: null,
  });

  const [newPrayerPlace, setNewPrayerPlace] = useState({
    faith_id: "",
    palace_name: "",
    master: "",
    address: "",
  });

  // ==========================================================
  // AUTH
  // ==========================================================

  const AuthToken = localStorage.getItem("AuthUser");

  axios.defaults.headers.common["Authorization"] =
    `Bearer ${AuthToken}`;

  // ==========================================================
  // INITIAL LOAD
  // ==========================================================

  useEffect(() => {
    fetchAffiliation();
  }, []);

  // ==========================================================
  // FETCH EXISTING AFFILIATION
  // ==========================================================

  const fetchAffiliation = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/religious-affiliations/${id}`
      );

      const affiliation = res.data.data;

      console.log("AFFILIATION:", affiliation);
      console.log("PEOPLE:", res.data.people);

      setFamilies(res.data.people || []);
      setFaiths(res.data.faiths || []);
      setPrayerPlaces(res.data.places || []);

      setAffiliationId(affiliation.id);

      // ==========================================
      // PERSON ID
      // ==========================================

      const existingPersonId =
        affiliation.person_id || "";

      // ==========================================
      // FIND PERSON
      // ==========================================

      const people = res.data?.people || [];
      const existingPerson = people.find(
        (person) =>
          String(person.person_id) === String(existingPersonId)
      );


      // ==========================================
      // SET SELECTED PERSON
      // ==========================================

      if (existingPerson) {
        setSelectedPerson({
          value: existingPerson.id,
          label:
            existingPerson.f_number

        });
      }

      // ==========================================
      // FAITH
      // ==========================================

      const existingFaithId =
        affiliation.place?.faith_id || "";

      // ==========================================
      // FORM
      // ==========================================

      setForm({
        person_id: existingPersonId,
        faith_id: existingFaithId,
        place_id:
          affiliation.place_id || "",
        baptized:
          affiliation.baptized ?? 0,
        comments:
          affiliation.comments || "",
        video:
          affiliation.video_link || null,
      });

      // ==========================================
      // GALLERY
      // ==========================================

      setImages(
        affiliation.images || []
      );

    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text: "Failed to load religious affiliation.",
        icon: "error",
      });
    }
  };
  // ==========================================================
  // SEARCHABLE PERSON OPTIONS
  // ==========================================================

  const personOptions = families.map((person) => ({
    value: person.id,
    label: person.f_number,
  }));

  // ==========================================================
  // PERSON CHANGE
  // ==========================================================

  const handlePersonChange = (selectedOption) => {
    setForm((prev) => ({
      ...prev,
      person_id: selectedOption
        ? selectedOption.value
        : "",
    }));

    setFormErrors((prev) => ({
      ...prev,
      person_id: "",
    }));
  };

  // ==========================================================
  // GENERAL CHANGE
  // ==========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    // When faith changes,
    // reset prayer place
    if (name === "faith_id") {
      setForm((prev) => ({
        ...prev,
        faith_id: value,
        place_id: "",
      }));

      setFormErrors((prev) => ({
        ...prev,
        faith_id: "",
        place_id: "",
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Remove error when user changes field
    setFormErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // ==========================================================
  // VALIDATION
  // ==========================================================

  const validate = () => {
    const errors = {};

    if (!form.person_id) {
      errors.person_id = "Person is required";
    }

    if (!form.faith_id) {
      errors.faith_id = "Faith is required";
    }

    if (!form.place_id) {
      errors.place_id = "Prayer Place is required";
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  // ==========================================================
  // UPDATE AFFILIATION
  // ==========================================================

  const submitForm = async (e) => {
    e.preventDefault();

    if (!validate()) {
      Swal.fire({
        title: "Error!",
        text: "Please fix the errors in the form.",
        icon: "error",
      });

      return;
    }

    try {
      const data = new FormData();

      // Laravel method spoofing
      data.append("_method", "PUT");

      data.append(
        "person_id",
        form.person_id
      );

      data.append(
        "place_id",
        form.place_id
      );

      data.append(
        "baptized",
        form.baptized
      );

      data.append(
        "comments",
        form.comments || ""
      );

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/religious-affiliations/${id}`,
        data,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );

      console.log(response.data);

      Swal.fire({
        title: "Success!",
        text: "Religious affiliation updated successfully.",
        icon: "success",
      });

      await fetchAffiliation();

    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          "Failed to update religious affiliation.",
        icon: "error",
      });
    }
  };
  // ==========================================================
  // ADD PRAYER PLACE
  // ==========================================================

  const savePrayerPlace = async () => {
    if (
      !newPrayerPlace.faith_id ||
      !newPrayerPlace.palace_name ||
      !newPrayerPlace.master ||
      !newPrayerPlace.address
    ) {
      Swal.fire({
        title: "Error!",
        text: "Please fill all prayer place fields.",
        icon: "error",
      });

      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/religious-affiliations/prayer-places`,
        newPrayerPlace
      );

      const created = res.data.data;

      // Add newly created place
      setPrayerPlaces((prev) => [
        ...prev,
        created,
      ]);

      // Automatically select new place
      setForm((prev) => ({
        ...prev,
        faith_id: created.faith_id,
        place_id: created.id,
      }));

      // Reset modal form
      setNewPrayerPlace({
        faith_id: "",
        palace_name: "",
        master: "",
        address: "",
      });

      // Remove validation errors
      setFormErrors((prev) => ({
        ...prev,
        faith_id: "",
        place_id: "",
      }));

      setShowPrayerPlaceModal(false);

      Swal.fire({
        title: "Success!",
        text: "Prayer place added successfully.",
        icon: "success",
      });

    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text:
          error?.response?.data?.message ||
          "Failed to add prayer place.",
        icon: "error",
      });
    }
  };

  // ==========================================================
  // VIDEO UPLOAD
  // ==========================================================

  const CHUNK_SIZE =
    5 * 1024 * 1024;

  const uploadVideoInChunks = async () => {
    if (!selectedVideo || !affiliationId) {
      Swal.fire({
        title: "Error!",
        text:
          "Please select video and religious affiliation.",
        icon: "error",
      });

      return;
    }

    setVideoUploading(true);
    setUploadProgress(0);

    try {
      const totalChunks = Math.ceil(
        selectedVideo.size /
        CHUNK_SIZE
      );

      for (
        let chunkIndex = 0;
        chunkIndex < totalChunks;
        chunkIndex++
      ) {
        const start =
          chunkIndex *
          CHUNK_SIZE;

        const end = Math.min(
          selectedVideo.size,
          start + CHUNK_SIZE
        );

        const chunk =
          selectedVideo.slice(
            start,
            end
          );

        const fd = new FormData();

        fd.append(
          "religious_affiliation_id",
          String(affiliationId)
        );

        fd.append(
          "chunk_index",
          String(chunkIndex)
        );

        fd.append(
          "total_chunks",
          String(totalChunks)
        );

        fd.append(
          "file_name",
          selectedVideo.name
        );

        fd.append(
          "video_chunk",
          chunk,
          `${chunkIndex}_${selectedVideo.name}`
        );

        await axios.post(
          `${process.env.REACT_APP_API_URL}/religious-affiliation-video-upload`,
          fd,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        setUploadProgress(
          Math.round(
            ((chunkIndex + 1) /
              totalChunks) *
            100
          )
        );
      }

      Swal.fire({
        title: "Success!",
        text: "Video uploaded successfully!",
        icon: "success",
      });

      setSelectedVideo(null);
      setUploadProgress(0);

      await fetchAffiliation();

    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error!",
        text: "Video upload failed.",
        icon: "error",
      });

    } finally {
      setVideoUploading(false);
    }
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (!affiliationId) {
    return (
      <PageLayout>
        <Card>
          <Card.Body>
            Loading...
          </Card.Body>
        </Card>
      </PageLayout>
    );
  }

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <PageLayout>

      {/* ======================================================
          MAIN FORM
      ====================================================== */}

      <Card>

        <Card.Body className="mb-3">

          <h4 className="mb-4">
            Edit Religious Affiliation
          </h4>

          <Form onSubmit={submitForm}>

            <Row>

              {/* ==================================================
                  PERSON
              ================================================== */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Person
                  </Form.Label>

                  <Select
                    options={personOptions}
                    value={selectedPerson}
                    onChange={(option) => {
                      setSelectedPerson(option);

                      setForm((prev) => ({
                        ...prev,
                        person_id: option?.value || "",
                      }));

                      setFormErrors((prev) => ({
                        ...prev,
                        person_id: "",
                      }));
                    }}
                    placeholder="Search and select person..."
                    isSearchable
                    isClearable
                    classNamePrefix="person-select"
                  />

                  {formErrors.person_id && (
                    <div className="invalid-feedback d-block">
                      {
                        formErrors.person_id
                      }
                    </div>
                  )}

                </Form.Group>

              </Col>

              {/* ==================================================
                  FAITH
              ================================================== */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Faith
                  </Form.Label>

                  <Form.Select
                    name="faith_id"
                    value={
                      form.faith_id
                    }
                    onChange={
                      handleChange
                    }
                    isInvalid={
                      !!formErrors.faith_id
                    }
                  >

                    <option value="">
                      Select Faith
                    </option>

                    {faiths.map(
                      (faith) => (
                        <option
                          key={
                            faith.id
                          }
                          value={
                            faith.id
                          }
                        >
                          {
                            faith.name
                          }
                        </option>
                      )
                    )}

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.faith_id
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>

              {/* ==================================================
                  PRAYER PLACE
              ================================================== */}

              <Col
                md={6}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Prayer Place
                  </Form.Label>

                  <Form.Select
                    name="place_id"
                    value={
                      form.place_id
                    }
                    onChange={(e) => {

                      const value =
                        e.target.value;

                      if (
                        value ===
                        "__add_new__"
                      ) {

                        setNewPrayerPlace(
                          {
                            faith_id:
                              form.faith_id,
                            palace_name:
                              "",
                            master:
                              "",
                            address:
                              "",
                          }
                        );

                        setShowPrayerPlaceModal(
                          true
                        );

                        return;
                      }

                      setForm(
                        (prev) => ({
                          ...prev,
                          place_id:
                            value,
                        })
                      );

                      setFormErrors(
                        (prev) => ({
                          ...prev,
                          place_id:
                            "",
                        })
                      );

                    }}
                    isInvalid={
                      !!formErrors.place_id
                    }
                  >

                    <option value="">
                      Select Prayer Place
                    </option>

                    {prayerPlaces
                      .filter(
                        (place) =>
                          !form.faith_id ||
                          Number(
                            place.faith_id
                          ) ===
                          Number(
                            form.faith_id
                          )
                      )
                      .map(
                        (place) => (
                          <option
                            key={
                              place.id
                            }
                            value={
                              place.id
                            }
                          >
                            {
                              place.palace_name
                            }
                          </option>
                        )
                      )}

                    <option value="__add_new__">
                      + Add New Prayer Place
                    </option>

                  </Form.Select>

                  <Form.Control.Feedback type="invalid">
                    {
                      formErrors.place_id
                    }
                  </Form.Control.Feedback>

                </Form.Group>

              </Col>

              {/* ==================================================
                  BAPTIZED
              ================================================== */}

              {
                form.faith_id === 2 || form.faith_id === "2" && (
                  <Col
                    md={6}
                    className="mb-3"
                  >

                    <Form.Group>

                      <Form.Label>
                        Baptized
                      </Form.Label>

                      <Form.Select
                        name="baptized"
                        value={
                          form.baptized
                        }
                        onChange={
                          handleChange
                        }
                      >

                        <option value="0">
                          No
                        </option>

                        <option value="1">
                          Yes
                        </option>

                      </Form.Select>

                    </Form.Group>

                  </Col>
                )
              }

              {/* ==================================================
                  COMMENTS
              ================================================== */}

              <Col
                md={12}
                className="mb-3"
              >

                <Form.Group>

                  <Form.Label>
                    Comments
                  </Form.Label>

                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="comments"
                    value={
                      form.comments
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="Enter comments"
                  />

                </Form.Group>

              </Col>

            </Row>

            <Button
              type="submit"
              className="mt-3"
            >
              Update Religious Affiliation
            </Button>

          </Form>

          {/* ======================================================
              GALLERY
          ====================================================== */}

          <CardHeader className="mt-5">

            <h5 className="text-center">
              Upload Picture Gallery
            </h5>

          </CardHeader>

          <GalleryUpload
            religiousAffiliationId={id}
            images={images}
          />

        </Card.Body>

      </Card>

      {/* ==========================================================
          VIDEO
      ========================================================== */}

      {affiliationId && (
        <Row>

          {/* ======================================================
              VIDEO UPLOAD
          ====================================================== */}

          <Col md={4}>

            <Card className="mt-4">

              <Card.Body>

                <CardHeader className="mb-3">

                  <h5>
                    Upload Religious Affiliation Video
                  </h5>

                </CardHeader>

                <Form.Control
                  type="file"
                  accept="video/*"
                  onChange={(e) => {

                    const file =
                      e.target.files?.[0];

                    setSelectedVideo(
                      file || null
                    );

                    setUploadProgress(
                      0
                    );
                  }}
                />

                {selectedVideo && (
                  <small className="d-block mt-2">
                    {
                      selectedVideo.name
                    }
                  </small>
                )}

                {uploadProgress > 0 && (

                  <div className="progress mt-3">

                    <div
                      className="progress-bar"
                      style={{
                        width: `${uploadProgress}%`,
                      }}
                    >
                      {uploadProgress}%
                    </div>

                  </div>

                )}

                <Button
                  className="mt-3"
                  disabled={
                    !selectedVideo ||
                    videoUploading
                  }
                  onClick={
                    uploadVideoInChunks
                  }
                >

                  {videoUploading
                    ? "Uploading..."
                    : "Upload Video"}

                </Button>

              </Card.Body>

            </Card>

          </Col>

          {/* ======================================================
              EXISTING VIDEO
          ====================================================== */}

          <Col md={8}>

            <Card className="mt-4">

              <CardBody>

                {form.video ? (

                  <>

                    <h5 className="mb-3">
                      Current Video
                    </h5>

                    <video
                      width="100%"
                      controls
                    >

                      <source
                        src={`${process.env.REACT_APP_IMG_URL}/${form.video}`}
                        type="video/mp4"
                      />

                      Your browser does not
                      support the video tag.

                    </video>

                  </>

                ) : (

                  <div className="text-center p-5">
                    No video uploaded yet.
                  </div>

                )}

              </CardBody>

            </Card>

          </Col>

        </Row>
      )}

      {/* ==========================================================
          ADD PRAYER PLACE MODAL
      ========================================================== */}

      <Modal
        show={
          showPrayerPlaceModal
        }
        onHide={() =>
          setShowPrayerPlaceModal(
            false
          )
        }
        centered
      >

        <Modal.Header
          closeButton
          className="m-3"
        >

          <Modal.Title>
            Add Prayer Place
          </Modal.Title>

        </Modal.Header>

        <Modal.Body className="m-3">

          {/* ==================================================
              FAITH
          ================================================== */}

          <Form.Group className="mb-3">

            <Form.Label>
              Faith
            </Form.Label>

            <Form.Select
              value={
                newPrayerPlace.faith_id
              }
              onChange={(e) =>
                setNewPrayerPlace(
                  (prev) => ({
                    ...prev,
                    faith_id:
                      e.target.value,
                  })
                )
              }
            >

              <option value="">
                Select Faith
              </option>

              {faiths.map(
                (faith) => (
                  <option
                    key={
                      faith.id
                    }
                    value={
                      faith.id
                    }
                  >
                    {
                      faith.name
                    }
                  </option>
                )
              )}

            </Form.Select>

          </Form.Group>

          {/* ==================================================
              PRAYER PLACE NAME
          ================================================== */}

          <Form.Group className="mb-3">

            <Form.Label>
              Prayer Place Name
            </Form.Label>

            <Form.Control
              type="text"
              placeholder="Enter prayer place name"
              value={
                newPrayerPlace.palace_name
              }
              onChange={(e) =>
                setNewPrayerPlace(
                  (prev) => ({
                    ...prev,
                    palace_name:
                      e.target.value,
                  })
                )
              }
            />

          </Form.Group>

          {/* ==================================================
              MASTER
          ================================================== */}

          <Form.Group className="mb-3">

            <Form.Label>
              Master
            </Form.Label>

            <Form.Control
              type="text"
              placeholder="Enter master name"
              value={
                newPrayerPlace.master
              }
              onChange={(e) =>
                setNewPrayerPlace(
                  (prev) => ({
                    ...prev,
                    master:
                      e.target.value,
                  })
                )
              }
            />

          </Form.Group>

          {/* ==================================================
              ADDRESS
          ================================================== */}

          <Form.Group className="mb-3">

            <Form.Label>
              Address
            </Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Enter address"
              value={
                newPrayerPlace.address
              }
              onChange={(e) =>
                setNewPrayerPlace(
                  (prev) => ({
                    ...prev,
                    address:
                      e.target.value,
                  })
                )
              }
            />

          </Form.Group>

        </Modal.Body>

        <Modal.Footer className="m-3">

          <Button
            variant="secondary"
            onClick={() =>
              setShowPrayerPlaceModal(
                false
              )
            }
          >
            Close
          </Button>

          <Button
            variant="primary"
            onClick={
              savePrayerPlace
            }
          >
            Save Prayer Place
          </Button>

        </Modal.Footer>

      </Modal>

    </PageLayout>
  );
}

